import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  Users, Search, MessageSquare, Send, Trash2, 
  UserPlus, UserMinus, Sparkles, Filter, 
  Calendar, CheckCircle2, History, Target,
  Phone, Mail, User, Variable, Trash, ArrowLeft,
  CheckCircle, Clock, ExternalLink, MessageCircle, RotateCcw, Plus, Trophy
} from 'lucide-react';
import AppButton from '@/components/ui/AppButton';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CampañasManager = () => {
  const [campañas, setCampañas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('list'); // 'create' | 'list' | 'detail'
  const [selectedCampaña, setSelectedCampaña] = useState(null);

  // Campaign Creation State
  const [campañaForm, setCampañaForm] = useState({
    nombre: '',
    descripcion: '',
    mensaje: 'Hola {{nombre}}, aún puedes participar en nuestros sorteos 🎁\n🔥 Últimos boletos disponibles, no te quedes fuera.'
  });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    fetchCampañas();
  }, []);

  const fetchCampañas = async () => {
    try {
      const res = await axios.get(`${API}/admin/campanas`);
      setCampañas(res.data);
    } catch (error) {
      console.error('Error fetching campanas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const res = await axios.post(`${API}/admin/buscar-usuarios`, { query });
      setSearchResults(res.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) handleSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  const applyFilter = async (tipo) => {
    setIsFiltering(true);
    try {
      const res = await axios.get(`${API}/admin/usuarios-filtrados?tipo=${tipo}`);
      const newUsers = res.data;
      
      setSelectedUsers(prev => {
        const existingIds = new Set(prev.map(u => u.id));
        const filtered = newUsers.filter(u => !existingIds.has(u.id));
        return [...prev, ...filtered];
      });
      
      toast.success(`${newUsers.length} usuarios identificados por filtro`);
    } catch (error) {
      toast.error('Error al aplicar filtro');
    } finally {
      setIsFiltering(false);
    }
  };

  const addUser = (user) => {
    const userId = user.id || user._id; // Soporte para ambos formatos
    if (!userId) {
      toast.error('Error: Usuario sin identificador válido');
      return;
    }
    
    if (selectedUsers.some(u => (u.id || u._id) === userId)) {
      toast.info('Usuario ya está en la lista');
      return;
    }
    setSelectedUsers(prev => [...prev, user]);
    setSearchQuery('');
    setSearchResults([]);
    toast.success(`${user.name || user.nombre} añadido`);
  };

  const removeUser = (userId) => {
    setSelectedUsers(prev => prev.filter(u => (u.id || u._id) !== userId));
  };

  const insertVariable = (variable) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = campañaForm.mensaje;
    const before = text.substring(0, start);
    const after = text.substring(end, text.length);
    
    setCampañaForm({
      ...campañaForm,
      mensaje: before + `{{${variable}}}` + after
    });

    // Restaurar foco y posición
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length + 4, start + variable.length + 4);
    }, 10);
  };

  const handleCreateCampaign = async () => {
    if (!campañaForm.nombre || selectedUsers.length === 0) {
      toast.error('Nombre y al menos un usuario son requeridos');
      return;
    }
    setIsCreating(true);
    try {
      await axios.post(`${API}/admin/campanas`, {
        nombre: campañaForm.nombre,
        descripcion: campañaForm.descripcion,
        mensaje_plantilla: campañaForm.mensaje,
        usuarios_ids: selectedUsers.map(u => u.id)
      });
      
      toast.success('Campaña guardada con éxito');
      setCampañaForm({ ...campañaForm, nombre: '', descripcion: '' });
      setSelectedUsers([]);
      fetchCampañas();
      setActiveTab('list');
    } catch (error) {
      toast.error('Error al crear campaña');
    } finally {
      setIsCreating(false);
    }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta campaña? No afectará a los usuarios ni sus compras.')) return;
    try {
      await axios.delete(`${API}/admin/campanas/${id}`);
      toast.success('Campaña eliminada');
      fetchCampañas();
      if (selectedCampaña?.id === id) {
        setActiveTab('list');
        setSelectedCampaña(null);
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const replaceVariables = (template, user) => {
    return template
      .replace(/\{\{nombre\}\}/g, user.nombre || 'Cliente')
      .replace(/\{\{email\}\}/g, user.email || '')
      .replace(/\{\{telefono\}\}/g, user.celular || '')
      .replace(/\{\{sorteo\}\}/g, ''); // Placeholder para sorteo
  };

  const formatWhatsAppNum = (celular) => {
    let n = celular ? celular.replace(/\D/g, '') : '';
    if (n.startsWith('0')) n = '593' + n.substring(1);
    else if (n.length === 9) n = '593' + n;
    return n;
  };

  const handleSendWhatsApp = async (campañaId, user) => {
    const num = formatWhatsAppNum(user.celular);
    const msg = replaceVariables(selectedCampaña.mensaje_plantilla, user);
    const url = `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
    
    // Abrir WhatsApp
    window.open(url, '_blank');

    // Actualizar estado en backend
    try {
      await axios.put(`${API}/admin/campanas/${campañaId}/usuario/${user.user_id}/enviado`);
      
      // Actualizar estado localmente
      setSelectedCampaña(prev => ({
        ...prev,
        envios: prev.envios.map(e => 
          e.user_id === user.user_id ? { ...e, estado: 'enviado', fecha_envio: new Date().toISOString() } : e
        )
      }));
      
      // Actualizar en el listado general también
      setCampañas(prev => prev.map(c => 
        c.id === campañaId 
          ? { ...c, envios: c.envios.map(e => e.user_id === user.user_id ? { ...e, estado: 'enviado' } : e) } 
          : c
      ));

    } catch (error) {
      console.error('Error al actualizar estado:', error);
      toast.error('No se pudo marcar como enviado en el sistema');
    }
  };

  const openCampañaDetail = (camp) => {
    setSelectedCampaña(camp);
    setActiveTab('detail');
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          {activeTab === 'detail' && (
            <button 
              onClick={() => setActiveTab('list')}
              className="h-12 w-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-all text-primary"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
          )}
          <div className="space-y-1">
            <h2 className="text-3xl font-black tracking-tight text-foreground uppercase">
              {activeTab === 'create' ? 'Configurar Campaña' : activeTab === 'detail' ? 'Ejecución de Campaña' : 'Mis Campañas'}
            </h2>
            <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest opacity-70">
              {activeTab === 'create' ? 'Define tu segmentación y mensaje base' : activeTab === 'detail' ? 'Envía mensajes uno a uno y trackea el progreso' : 'Historial de campañas y seguimiento de envíos'}
            </p>
          </div>
        </div>
        
        {activeTab !== 'create' && (
          <AppButton 
            onClick={() => setActiveTab('create')}
            variant="primary"
            icon={Plus}
            className="h-12 px-6 rounded-2xl font-black text-xs uppercase"
          >
            Nueva Campaña
          </AppButton>
        )}
      </div>

      {activeTab === 'create' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-in slide-in-from-bottom-4 duration-500">
          {/* Config Column */}
          <div className="lg:col-span-12 space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Basic Info */}
              <Card className="border-none shadow-2xl bg-card/50 backdrop-blur-xl ring-1 ring-white/10 rounded-[2.5rem] overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-lg font-black uppercase tracking-tight flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-orange-500" /> Datos de Identificación
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Nombre de Campaña *</Label>
                    <Input 
                      placeholder="Ej: Promo Fin de Semana" 
                      value={campañaForm.nombre}
                      onChange={e => setCampañaForm({...campañaForm, nombre: e.target.value})}
                      className="h-14 rounded-2xl bg-black/[0.03] dark:bg-white/5 border-white/10 focus:ring-primary/20 font-bold text-lg"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Descripción Interna (Opcional)</Label>
                    <Input 
                      placeholder="Para uso administrativo..." 
                      value={campañaForm.descripcion}
                      onChange={e => setCampañaForm({...campañaForm, descripcion: e.target.value})}
                      className="h-14 rounded-2xl bg-black/[0.03] dark:bg-white/5 border-white/10 focus:ring-primary/20 font-medium"
                    />
                  </div>

                  <div className="pt-4 space-y-4">
                    <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1 flex items-center gap-2">
                      <Filter className="w-3 h-3" /> Selección de Audiencia
                    </Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {[
                        { id: 'nuevos_sin_compra', label: 'Usuarios Nuevos', icon: UserPlus },
                        { id: 'compradores', label: 'Compradores', icon: CheckCircle2 },
                        { id: 'recientes', label: 'Recientes (15d)', icon: History },
                        { id: 'inactivos', label: 'Inactivos', icon: Trash2 },
                      ].map(filter => (
                        <button
                          key={filter.id}
                          disabled={isFiltering}
                          onClick={() => applyFilter(filter.id)}
                          className="flex items-center gap-3 p-4 rounded-2xl bg-white/5 border border-white/5 hover:bg-primary/10 hover:border-primary/20 transition-all text-left group"
                        >
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <filter.icon className="w-4 h-4 text-primary" />
                          </div>
                          <span className="text-[10px] font-black uppercase tracking-tight text-foreground">{filter.label}</span>
                        </button>
                      ))}
                    </div>
                    
                    {/* Manual Search */}
                    <div className="relative pt-2">
                      <Search className="absolute left-4 top-[2.1rem] w-4 h-4 text-muted-foreground" />
                      <Input 
                        placeholder="Buscar por nombre, email o cédula..." 
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="h-12 pl-12 rounded-xl bg-black/[0.03] dark:bg-white/5 border-white/10 text-xs"
                      />
                      {searchResults.length > 0 && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-white/10 rounded-2xl shadow-2xl z-50 max-h-48 overflow-y-auto p-2">
                          {searchResults.map(user => (
                            <button
                              key={user.id || user._id}
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault(); // Evita que el input pierda foco antes del evento
                                addUser(user);
                              }}
                              className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-primary/10 transition-colors text-left border-b border-white/5 last:border-0"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center font-black text-primary text-xs">
                                  {(user.name || user.nombre || "?").charAt(0)}
                                </div>
                                <div>
                                  <p className="text-[11px] font-bold text-foreground">
                                    {user.name || user.nombre}
                                  </p>
                                  <p className="text-[9px] text-muted-foreground font-medium">
                                    {user.celular || user.email}
                                  </p>
                                </div>
                              </div>
                              <Plus className="w-4 h-4 text-primary" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-primary/10 rounded-2xl border border-primary/20">
                        <div className="flex items-center gap-3">
                           <Users className="w-5 h-5 text-primary" />
                           <div>
                              <p className="text-xs font-black uppercase tracking-tight">{selectedUsers.length} Usuarios Seleccionados</p>
                              <p className="text-[9px] font-bold text-muted-foreground uppercase">Audiencia congelada para envío</p>
                           </div>
                        </div>
                        <AppButton variant="ghost" size="sm" onClick={() => setSelectedUsers([])} className="h-8 text-[10px] font-black uppercase tracking-widest px-4">Vaciar</AppButton>
                      </div>

                      {/* Lista visual de usuarios seleccionados */}
                      {selectedUsers.length > 0 && (
                        <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border border-white/5 rounded-2xl bg-black/5">
                          {selectedUsers.map(u => (
                            <div 
                              key={u.id || u._id} 
                              className="flex items-center gap-2 px-3 py-1.5 bg-card/80 border border-white/10 rounded-xl animate-in zoom-in-95 duration-200"
                            >
                              <span className="text-[10px] font-black tracking-tight text-foreground/80 lowercase italic">@{u.id?.substring(0,4) || 'u'}</span>
                              <span className="text-[10px] font-bold truncate max-w-[120px] uppercase tracking-tighter">{u.nombre || u.name}</span>
                              <button 
                                onClick={() => removeUser(u.id || u._id)}
                                className="w-5 h-5 rounded-full hover:bg-red-500/20 text-muted-foreground hover:text-red-500 transition-all flex items-center justify-center"
                              >
                                <Plus className="w-3 h-3 rotate-45" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Message Editor */}
              <Card className="border-none shadow-2xl bg-card/50 backdrop-blur-xl ring-1 ring-white/10 rounded-[2.5rem] overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-lg font-black uppercase tracking-tight flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-primary" /> Redacción Estrecha
                    </div>
                  </CardTitle>
                  <CardDescription className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                    <Variable className="w-3 h-3" /> Haz click en las variables para insertarlas
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Variable Buttons */}
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 'nombre', label: 'Nombre', icon: User },
                      { id: 'telefono', label: 'Celular', icon: Phone },
                      { id: 'email', label: 'Email', icon: Mail },
                      { id: 'sorteo', label: 'Nombre Sorteo', icon: Trophy },
                    ].map(v => (
                      <button
                        key={v.id}
                        onClick={() => insertVariable(v.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-orange-500/10 border border-orange-500/20 hover:bg-orange-500/20 transition-all group"
                      >
                        <v.icon className="w-3 h-3 text-orange-500" />
                        <span className="text-[10px] font-black text-orange-500 uppercase tracking-widest">{"{{"}{v.id}{"}}"}</span>
                      </button>
                    ))}
                  </div>

                  <Textarea 
                    ref={textareaRef}
                    value={campañaForm.mensaje}
                    onChange={e => setCampañaForm({...campañaForm, mensaje: e.target.value})}
                    className="min-h-[280px] rounded-3xl bg-black/[0.03] dark:bg-white/5 border-white/10 focus:ring-primary/20 p-6 text-sm font-medium leading-relaxed resize-none shadow-inner"
                    placeholder="Escribe tu mensaje aquí..."
                  />

                  <div className="flex gap-4">
                    <AppButton 
                      onClick={handleCreateCampaign}
                      loading={isCreating}
                      disabled={selectedUsers.length === 0 || !campañaForm.nombre}
                      variant="primary"
                      size="lg"
                      icon={Send}
                      className="flex-1 h-16 rounded-[1.5rem] shadow-xl shadow-primary/20 text-lg font-black uppercase"
                    >
                      Guardar Campaña
                    </AppButton>
                    <AppButton 
                      onClick={() => setActiveTab('list')}
                      variant="ghost"
                      className="h-16 px-8 rounded-[1.5rem] font-bold uppercase text-xs"
                    >
                      Cancelar
                    </AppButton>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'list' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in slide-in-from-bottom-4 duration-500">
          {campañas.length === 0 ? (
             <div className="col-span-full py-24 text-center bg-card/50 rounded-[3rem] border border-white/5">
                <History className="w-16 h-16 text-muted-foreground/20 mx-auto mb-4" />
                <h3 className="text-xl font-black text-foreground uppercase">No hay campañas históricas</h3>
                <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest mt-1">Configura tu primera campaña para comenzar</p>
             </div>
          ) : (
            campañas.map(camp => {
              const enviadosCount = camp.envios?.filter(e => e.estado === 'enviado').length || 0;
              const totalCount = camp.envios?.length || 0;
              const porcentaje = totalCount > 0 ? (enviadosCount / totalCount) * 100 : 0;

              return (
                <Card key={camp.id} className="border-none shadow-xl bg-card/50 backdrop-blur-xl ring-1 ring-white/10 rounded-[2rem] overflow-hidden group hover:ring-primary/30 transition-all flex flex-col">
                  <div className="p-6 space-y-4 flex-1">
                    <div className="flex justify-between items-start">
                      <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                        <MessageSquare className="w-6 h-6" />
                      </div>
                      <Badge className="bg-primary/10 text-primary border-primary/20 font-black h-6 px-3">{totalCount} USUARIOS</Badge>
                    </div>
                    
                    <div>
                      <h4 className="text-xl font-black tracking-tight uppercase truncate">{camp.nombre}</h4>
                      <p className="text-xs text-muted-foreground font-medium line-clamp-1 opacity-70 mt-1">{camp.descripcion || 'Sin descripción'}</p>
                    </div>

                    <div className="flex items-center gap-3 text-[10px] text-muted-foreground font-bold uppercase tracking-widest">
                       <Calendar className="w-3.5 h-3.5 opacity-50" /> {formatDateTime(camp.created_at)}
                    </div>

                    {/* Progress Bar Mini */}
                    <div className="space-y-1.5 pt-2">
                       <div className="flex justify-between text-[9px] font-black uppercase tracking-widest">
                          <span className={cn(porcentaje === 100 ? "text-green-500" : "text-primary")}>Progreso: {porcentaje.toFixed(0)}%</span>
                          <span>{enviadosCount}/{totalCount} Enviados</span>
                       </div>
                       <div className="h-1.5 bg-black/10 dark:bg-white/5 rounded-full overflow-hidden">
                          <div 
                            className={cn("h-full transition-all duration-1000", porcentaje === 100 ? "bg-green-500" : "bg-primary")} 
                            style={{ width: `${porcentaje}%` }} 
                          />
                       </div>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-black/5 flex gap-2 border-t border-white/5">
                    <AppButton 
                      onClick={() => openCampañaDetail(camp)}
                      className="flex-1 h-12 rounded-xl font-black text-xs uppercase"
                      variant="secondary"
                      icon={ExternalLink}
                    >
                      Ejecutar
                    </AppButton>
                    <button 
                      onClick={() => deleteCampaign(camp.id)}
                      className="h-12 w-12 rounded-xl bg-red-500/10 text-red-500 flex items-center justify-center hover:bg-red-500 hover:text-white transition-all"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      )}

      {activeTab === 'detail' && selectedCampaña && (
        <div className="space-y-6 animate-in zoom-in-95 duration-500">
           {/* Campaign Summary Widget */}
           <Card className="border-none shadow-2xl bg-card/80 backdrop-blur-2xl ring-1 ring-white/10 rounded-[2.5rem] overflow-hidden">
             <div className="p-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                   <div className="space-y-2">
                      <div className="flex items-center gap-3">
                         <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                            <Send className="w-5 h-5" />
                         </div>
                         <h3 className="text-2xl font-black uppercase tracking-tight">{selectedCampaña.nombre}</h3>
                      </div>
                      <p className="text-sm font-medium text-muted-foreground max-w-xl leading-relaxed italic opacity-80 border-l-4 border-primary/20 pl-4">
                        "{selectedCampaña.mensaje_plantilla}"
                      </p>
                   </div>
                   
                   <div className="flex flex-row md:flex-col items-center md:items-end gap-6 bg-primary/5 p-4 md:p-6 rounded-3xl border border-primary/10">
                      <div className="text-center md:text-right">
                         <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground mb-1">Efectividad Envío</p>
                         <div className="flex items-baseline gap-1">
                            <span className="text-4xl font-black text-primary">
                              {((selectedCampaña.envios?.filter(e => e.estado === 'enviado').length / selectedCampaña.envios?.length) * 100).toFixed(0)}%
                            </span>
                            <span className="text-xs font-bold text-muted-foreground">de {selectedCampaña.envios?.length}</span>
                         </div>
                      </div>
                   </div>
                </div>
             </div>
           </Card>

           {/* User CRM List */}
           <Card className="border-none shadow-2xl bg-card/50 backdrop-blur-xl ring-1 ring-white/10 rounded-[2.5rem] overflow-hidden">
              <CardHeader className="border-b border-white/5 space-y-4">
                 <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <CardTitle className="text-lg font-black uppercase tracking-tight flex items-center gap-2">
                      <Users className="w-5 h-5 text-primary" /> Lista de Seguimiento
                    </CardTitle>
                    <div className="flex items-center gap-2">
                       <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 font-black h-7">
                         {selectedCampaña.envios?.filter(e => e.estado === 'pendiente').length} PENDIENTES
                       </Badge>
                       <Badge className="bg-green-500/10 text-green-500 border-green-500/20 font-black h-7">
                         {selectedCampaña.envios?.filter(e => e.estado === 'enviado').length} COMPLETADOS
                       </Badge>
                    </div>
                 </div>
              </CardHeader>
              <CardContent className="p-0">
                 <div className="overflow-x-auto">
                    <table className="w-full text-left">
                       <thead>
                          <tr className="border-b border-white/5 bg-black/5">
                             <th className="p-6 text-[10px] font-black uppercase tracking-widest text-muted-foreground">Usuario</th>
                             <th className="p-6 text-[10px] font-black uppercase tracking-widest text-muted-foreground">Contacto</th>
                             <th className="p-6 text-[10px] font-black uppercase tracking-widest text-muted-foreground">Estado CRM</th>
                             <th className="p-6 text-right text-[10px] font-black uppercase tracking-widest text-muted-foreground">Acción</th>
                          </tr>
                       </thead>
                       <tbody className="divide-y divide-white/5">
                          {selectedCampaña.envios?.map(user => (
                             <tr key={user.user_id} className="hover:bg-primary/5 transition-colors group">
                                <td className="p-6">
                                   <div className="flex items-center gap-4">
                                      <div className={cn(
                                        "w-12 h-12 rounded-2xl flex items-center justify-center font-black text-xs transition-all",
                                        user.estado === 'enviado' ? "bg-green-500/20 text-green-500" : "bg-white/10 text-foreground"
                                      )}>
                                         {user.nombre.charAt(0)}
                                      </div>
                                      <div>
                                         <p className="text-sm font-black uppercase tracking-tight">{user.nombre}</p>
                                         <p className="text-[10px] font-bold text-muted-foreground uppercase opacity-70">ID: {user.user_id.substring(0, 8)}</p>
                                      </div>
                                   </div>
                                </td>
                                <td className="p-6">
                                   <div className="space-y-1">
                                      <div className="flex items-center gap-2 text-xs font-medium text-foreground">
                                         <Phone className="w-3.5 h-3.5 text-primary" /> {user.celular}
                                      </div>
                                      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground opacity-70">
                                         <Mail className="w-3.5 h-3.5" /> {user.email}
                                      </div>
                                   </div>
                                </td>
                                <td className="p-6">
                                   {user.estado === 'enviado' ? (
                                      <div className="flex flex-col gap-1">
                                         <Badge className="bg-green-500 text-white border-none font-black text-[9px] h-6 px-3 shadow-lg shadow-green-500/20">
                                           <CheckCircle className="w-3 h-3 mr-1.5" /> ENVIADO
                                         </Badge>
                                         {user.fecha_envio && (
                                           <span className="text-[9px] font-bold text-muted-foreground ml-1">
                                              {new Date(user.fecha_envio).toLocaleDateString()} {new Date(user.fecha_envio).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                           </span>
                                         )}
                                      </div>
                                   ) : (
                                      <Badge variant="outline" className="text-muted-foreground border-white/10 font-bold text-[9px] h-6 px-3">
                                         <Clock className="w-3 h-3 mr-1.5 opacity-50" /> PENDIENTE
                                      </Badge>
                                   )}
                                </td>
                                <td className="p-6 text-right">
                                   {user.estado === 'enviado' ? (
                                      <button 
                                        onClick={() => handleSendWhatsApp(selectedCampaña.id, user)}
                                        className="h-11 px-4 rounded-xl bg-black/10 text-muted-foreground font-black text-[10px] uppercase flex items-center gap-2 ml-auto hover:bg-black/20 transition-all"
                                      >
                                         <RotateCcw className="w-3.5 h-3.5" /> Re-enviar
                                      </button>
                                   ) : (
                                      <AppButton 
                                        variant="primary"
                                        size="sm"
                                        onClick={() => handleSendWhatsApp(selectedCampaña.id, user)}
                                        className="h-11 px-6 rounded-xl font-black text-[10px] uppercase shadow-lg shadow-primary/20"
                                        icon={MessageCircle}
                                      >
                                         Enviar a WhatsApp
                                      </AppButton>
                                   )}
                                </td>
                             </tr>
                          ))}
                       </tbody>
                    </table>
                 </div>
              </CardContent>
           </Card>
        </div>
      )}
    </div>
  );
};

export default CampañasManager;
